import { Injectable } from '@angular/core';
import { PDFDocument, PDFImage } from 'pdf-lib';
import {
  SIGNATURE_BOX_HEIGHT_PT,
  SIGNATURE_BOX_WIDTH_PT,
} from '../shared-components/signature-box.util';

/**
 * The authorised frame wraps the signature box: the box itself forms the
 * frame's upper part, and the strip below the box carries the "Authorised
 * Signatory" line. The strip is what keeps that line clear of the Aadhaar/DSC
 * stamp, which is printed resting on the box's bottom edge.
 *
 * The frame's width always comes from the box, so Drag & Drop and Pre-Defined
 * (both 110x50pt boxes) produce an identically sized frame.
 */
const AUTHORISED_FRAME_LABEL_STRIP_PT = 15;

/**
 * Frame canvas. The height follows the frame's aspect ratio so the labels
 * scale evenly instead of being stretched, and the padding and font sizes are
 * scaled off this base so a label comes out the same physical size on the page
 * whatever canvas height that produces.
 */
const AUTHORISED_FRAME_CANVAS_WIDTH = 400;
const AUTHORISED_FRAME_BASE_CANVAS_HEIGHT = 160;
const AUTHORISED_FRAME_BASE_LABEL_PADDING = 5;
const AUTHORISED_FRAME_BASE_TOP_FONT = 24;
const AUTHORISED_FRAME_BASE_BOTTOM_FONT = 22;

@Injectable({
  providedIn: 'root',
})
export class ImageGeneratorService {
  // Blank frame for an authorized  Aadhaar/DSC signatory: "For: {name}" on
  // top, empty middle for the eventual emudhra signature, "Authorised
  // Signatory" on the bottom.
  //
  // `aspectRatio` is the width/height of the rect this frame will be drawn
  // into. The canvas is shaped to match it so pdf-lib scales the labels evenly
  // rather than stretching them; the padding and fonts scale with the canvas so
  // the labels keep the same proportions and land in the same place.
  generateAuthorisedFrame(authorisedFor: string, aspectRatio?: number): string {
    const defaultAspectRatio =
      AUTHORISED_FRAME_CANVAS_WIDTH / AUTHORISED_FRAME_BASE_CANVAS_HEIGHT;
    const aspect =
      typeof aspectRatio === 'number' &&
      Number.isFinite(aspectRatio) &&
      aspectRatio > 0
        ? aspectRatio
        : defaultAspectRatio;

    const canvas = document.createElement('canvas');
    canvas.width = AUTHORISED_FRAME_CANVAS_WIDTH;
    canvas.height = Math.max(
      1,
      Math.round(AUTHORISED_FRAME_CANVAS_WIDTH / aspect),
    );

    const canvasScale = canvas.height / AUTHORISED_FRAME_BASE_CANVAS_HEIGHT;
    const padding = Math.round(
      AUTHORISED_FRAME_BASE_LABEL_PADDING * canvasScale,
    );
    const topFontSize = Math.round(
      AUTHORISED_FRAME_BASE_TOP_FONT * canvasScale,
    );
    const bottomFontSize = Math.round(
      AUTHORISED_FRAME_BASE_BOTTOM_FONT * canvasScale,
    );

    const ctx = canvas.getContext('2d');

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.textAlign = 'left';
    ctx.fillStyle = '#1a56db';

    const forText = `For ${authorisedFor}`;
    this.fitBoldFont(ctx, forText, topFontSize, canvas.width - padding * 2);
    ctx.textBaseline = 'top';
    ctx.fillText(forText, padding, padding);

    ctx.font = `bold ${bottomFontSize}px Arial`;
    ctx.textBaseline = 'bottom';
    ctx.fillText('Authorised Signatory', padding, canvas.height - padding);

    return canvas.toDataURL('image/png');
  }

  // Bold Arial at `maxSize`, shrunk only as far as needed for `text` to fit
  // in `maxWidth`, so a long "Authorised For" name is never cut off.
  private fitBoldFont(ctx: CanvasRenderingContext2D, text: string, maxSize: number, maxWidth: number): void {
    ctx.font = `bold ${maxSize}px Arial`;
    const width = ctx.measureText(text).width;
    if (width <= maxWidth) {
      return;
    }
    let size = Math.floor((maxSize * maxWidth) / width);
    ctx.font = `bold ${size}px Arial`;
    while (size > 1 && ctx.measureText(text).width > maxWidth) {
      size -= 1;
      ctx.font = `bold ${size}px Arial`;
    }
  }

  generateAuthorisedFrameWithSignature(base64Image: string, authorisedFor: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const image = new Image();

      image.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 500;
        canvas.height = 260;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject('Failed to get canvas context');
          return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        const topLabelHeight = 32;
        const bottomLabelHeight = 32;
        const sideMargin = 5;
        const availableWidth = canvas.width - sideMargin * 2;
        const availableHeight = canvas.height - topLabelHeight - bottomLabelHeight;

        const scale = Math.min(availableWidth / image.width, availableHeight / image.height);
        const drawWidth = image.width * scale;
        const drawHeight = image.height * scale;
        const drawX = (canvas.width - drawWidth) / 2;
        const drawY = topLabelHeight + (availableHeight - drawHeight) / 2;

        ctx.drawImage(image, drawX, drawY, drawWidth, drawHeight);

        ctx.fillStyle = '#1a56db';
        ctx.textAlign = 'left';

        const forText = `For ${authorisedFor}`;
        this.fitBoldFont(ctx, forText, 34, canvas.width - sideMargin * 2);
        ctx.textBaseline = 'top';
        ctx.fillText(forText, 5, 3);

        ctx.font = 'bold 32px Arial';
        ctx.textBaseline = 'bottom';
        ctx.fillText('Authorised Signatory', 5, canvas.height - 3);

        resolve(canvas.toDataURL('image/png'));
      };

      image.onerror = () => {
        reject('Failed to load image');
      };

      image.src = base64Image;
    });
  }

  async mergeAuthorisedFramesIntoPdf(pdfBase64: string, parties: any[]): Promise<string> {
    const authorisedParties = (parties || []).filter(
      (party: any) =>
        party?.is_authorised &&
        party?.authorised_for &&
        ['AADHAAR', 'DSC'].includes((party.method || '').toUpperCase()),
    );

    if (authorisedParties.length === 0) {
      return pdfBase64;
    }

    // pdfBase64 sometimes arrives as a full `data:application/pdf;base64,...`
    // URI rather than raw base64, depending on the upload flow — strip the
    // prefix (and any stray whitespace) before decoding either way.
    const cleanedPdfBase64 = (pdfBase64.includes(',') ? pdfBase64.split(',').pop() : pdfBase64).replace(/\s/g, '');
    const pdfBytes = Uint8Array.from(atob(cleanedPdfBase64), (c) => c.charCodeAt(0));
    const pdfDoc = await PDFDocument.load(pdfBytes);
    const pages = pdfDoc.getPages();

    for (const party of authorisedParties) {
      // One embedded image per frame shape. Every box is the same size in
      // practice, so this is a single image; keying by shape just avoids
      // reusing a frame built for a different aspect ratio.
      const framesByAspect = new Map<string, PDFImage>();

      for (const [pageKey, boxes] of Object.entries(party.position_details || {})) {
        const page = pages[Number(pageKey) - 1];
        if (!page || !Array.isArray(boxes)) continue;

        const pageWidth = page.getWidth();
        const pageHeight = page.getHeight();

        for (const box of boxes as any[]) {
          // Make room below the box for the "Authorised Signatory" strip, so
          // it can't fall off the bottom edge of the page.
          this.liftBoxForAuthorisedFrame(box, pageHeight);

          const frame = this.getAuthorisedFrameRect(box, pageWidth, pageHeight);
          if (!frame) continue;

          const aspect = frame.width / frame.height;
          const aspectKey = aspect.toFixed(3);
          let embeddedImage = framesByAspect.get(aspectKey);
          if (!embeddedImage) {
            const frameDataUrl = this.generateAuthorisedFrame(
              party.authorised_for,
              aspect,
            );
            const frameBytes = Uint8Array.from(
              atob(frameDataUrl.split(',')[1]),
              (c) => c.charCodeAt(0),
            );
            embeddedImage = await pdfDoc.embedPng(frameBytes);
            framesByAspect.set(aspectKey, embeddedImage);
          }

          page.drawImage(embeddedImage, frame);
        }
      }
    }

    return pdfDoc.saveAsBase64();
  }

  /**
   * Where the authorised frame is drawn: the signature box's width, and its
   * height plus the label strip, anchored so the strip sits directly below the
   * box.
   *
   * Taking the width from the box is what makes the frame the same size for
   * Drag & Drop and Pre-Defined, and it replaces a fixed 140pt width that was
   * wider than the 110pt box and had to be scaled down at BOTTOM_RIGHT (where
   * 140pt overflowed the page), so the same position rendered at a different
   * size from the others.
   *
   * Only authorised Aadhaar/DSC boxes reach this. Normal placement, and
   * Electronic (which draws its labels inside the signature image itself), are
   * untouched.
   */
  private getAuthorisedFrameRect(
    box: any,
    pageWidth: number,
    pageHeight: number,
  ): { x: number; y: number; width: number; height: number } | null {
    const x1 = Number(box?.x1);
    const y1 = Number(box?.y1);
    if (!Number.isFinite(x1) || !Number.isFinite(y1)) {
      return null;
    }

    // Fall back to the standard box when the stored rect isn't a rectangle.
    let boxWidth = Number(box?.x2) - x1;
    let boxHeight = Number(box?.y2) - y1;
    if (!Number.isFinite(boxWidth) || boxWidth <= 0) {
      boxWidth = SIGNATURE_BOX_WIDTH_PT;
    }
    if (!Number.isFinite(boxHeight) || boxHeight <= 0) {
      boxHeight = SIGNATURE_BOX_HEIGHT_PT;
    }

    // The box is already clamped to the page by the placement code; capping
    // here just guards a page smaller than the frame, or odd stored values.
    const width = Math.min(boxWidth, pageWidth);
    const height = Math.min(
      boxHeight + AUTHORISED_FRAME_LABEL_STRIP_PT,
      pageHeight,
    );
    if (width <= 0 || height <= 0) {
      return null;
    }

    return {
      x: Math.max(0, Math.min(x1, pageWidth - width)),
      y: Math.max(
        0,
        Math.min(y1 - AUTHORISED_FRAME_LABEL_STRIP_PT, pageHeight - height),
      ),
      width,
      height,
    };
  }

  /**
   * Makes room under a signature box for the frame's "Authorised Signatory"
   * strip.
   *
   * Every BOTTOM_* position sits only 5pt above the page edge, and a Drag &
   * Drop box can be dropped flush against it, leaving less than the strip
   * needs — which is what pushed that line off the page and printed it cut
   * off. Lifting the box by the shortfall keeps the line on the page and keeps
   * it below the signature rather than printing across it.
   *
   * The box is mutated in place so the signature is stamped where the frame is
   * drawn around it; the caller passes the same `position_details` that is
   * submitted with the order. The target is a fixed offset, so running this
   * twice on the same box is a no-op.
   */
  private liftBoxForAuthorisedFrame(box: any, pageHeight: number): void {
    const y1 = Number(box?.y1);
    const y2 = Number(box?.y2);
    if (!Number.isFinite(y1) || !Number.isFinite(y2)) {
      return;
    }

    const boxHeight = y2 - y1;
    if (boxHeight <= 0) {
      return;
    }

    // Don't push the box off the top edge on a page too short to hold both.
    const targetY1 = Math.min(
      AUTHORISED_FRAME_LABEL_STRIP_PT,
      Math.max(0, pageHeight - boxHeight),
    );
    if (y1 >= targetY1) {
      return;
    }

    box.y1 = targetY1;
    box.y2 = targetY1 + boxHeight;
  }

  generateImage(
    text: string,
    style: string,
    width: number = 600,
    height: number = 250
  ) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');

    // Set semi-transparent white background
    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Set text properties
    ctx.font = style;
    ctx.fillStyle = 'black';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';

    // Draw text with a margin of 20 pixels from the left
    ctx.fillText(text, 20, canvas.height / 2);

    // Convert the canvas content to a data URL in PNG format with the specified background and text color
    return canvas.toDataURL('image/png');
  }

  async addBordersAndText(
    base64Image: string,
    topText: string,
    bottomText: string
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      const image = new Image();

      image.onload = () => {
        // Tight around the stamp, so it prints from the bottom of its box like
        // Aadhaar's. It used to be 600x200 with ~25px left empty under the ID
        // line, which lifted the electronic stamp above Aadhaar's.
        const canvas = document.createElement('canvas');
        canvas.width = 600;
        canvas.height = 180;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject('Failed to get canvas context');
          return;
        }

        // Clear canvas with transparent background
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Text lines are 36px (was 30px); a line too long for the width is
        // shrunk just enough to fit instead of being cut off.
        const textMaxWidth = canvas.width - 10;
        const setFittedFont = (text: string) => {
          let size = 36;
          ctx.font = `${size}px Cambria`;
          while (size > 20 && ctx.measureText(text).width > textMaxWidth) {
            size--;
            ctx.font = `${size}px Cambria`;
          }
        };

        // Draw the signature image between the two text lines (it slightly
        // overlaps the top line, as before).
        const imageTop = 24;
        const bottomTextHeight = 40;
        const imageHeight = canvas.height - imageTop - bottomTextHeight;
        ctx.drawImage(image, 0, imageTop, canvas.width, imageHeight);

        // Draw top text (no border)
        ctx.fillStyle = 'black';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        setFittedFont(topText);
        ctx.fillText(topText, 5, 0);

        // Draw left border only
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(0, imageTop);
        ctx.lineTo(0, imageTop + imageHeight);
        ctx.stroke();

        // Draw bottom text aligned to left border (no bottom border), resting on
        // the image's bottom edge so no blank band is left below it.
        ctx.textAlign = 'left';
        ctx.textBaseline = 'bottom';
        setFittedFont(bottomText);
        ctx.fillText(bottomText, 5, canvas.height - 1);

        resolve(canvas.toDataURL('image/png'));
      };

      image.onerror = () => {
        reject('Failed to load image');
      };

      image.src = base64Image;
    });
  }

  /**
   * The copy of a stamp that gets placed on the PDF: padded with transparent
   * space to the signature box's own shape (SIGNATURE_BOX_WIDTH_PT x
   * SIGNATURE_BOX_HEIGHT_PT), the stamp resting on the bottom edge.
   *
   * The image is fitted into its box keeping its proportions and centred. The
   * 600x180 electronic stamp is wider than the 110x50 box, so it came out 110x33
   * and floated ~8.5pt above the box bottom, while an Aadhaar stamp sits on it.
   * With the box's shape it still prints 110x33, but on the bottom edge, so both
   * land on one line. Coordinates and on-screen previews don't change.
   */
  padToSignatureBox(base64Image: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => {
        const boxRatio = SIGNATURE_BOX_WIDTH_PT / SIGNATURE_BOX_HEIGHT_PT;
        const imageRatio = image.width / image.height;
        const canvas = document.createElement('canvas');
        // Wider than the box: add space on top. Taller: add space at the sides
        // (centred, as it was already placed).
        canvas.width = imageRatio >= boxRatio ? image.width : Math.round(image.height * boxRatio);
        canvas.height = imageRatio >= boxRatio ? Math.round(image.width / boxRatio) : image.height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject('Failed to get canvas context');
          return;
        }
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        // One practically invisible pixel in the top corner keeps the padding
        // from being trimmed away as "empty" when the image is processed.
        ctx.fillStyle = 'rgba(255, 255, 255, 0.02)';
        ctx.fillRect(canvas.width - 1, 0, 1, 1);
        ctx.drawImage(
          image,
          Math.round((canvas.width - image.width) / 2),
          canvas.height - image.height,
        );
        resolve(canvas.toDataURL('image/png'));
      };
      image.onerror = () => reject('Failed to load image');
      image.src = base64Image;
    });
  }

  checkIfImageIsBlank(base64Image: string): Promise<boolean> {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = base64Image;

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const pixels = imageData.data;

        let hasVisibleContent = false;
        for (let i = 0; i < pixels.length; i += 4) {
          const alpha = pixels[i + 3];
          if (alpha !== 0) {
            hasVisibleContent = true;
            break;
          }
        }

        resolve(!hasVisibleContent);
      };

      img.onerror = () => resolve(true);
    });
  }
  addSignature(base64Image: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 500;
        canvas.height = 200;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject('Failed to get canvas context');
          return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height - 20);

        // Left border
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(0, 20);
        ctx.lineTo(0, canvas.height - 20);
        ctx.stroke();

        // Bottom border
        ctx.beginPath();
        ctx.moveTo(0, canvas.height - 20);
        ctx.stroke();

        ctx.font = '20px Cambria';
        ctx.textAlign = 'right';

        resolve(canvas.toDataURL('image/png'));
      };

      image.onerror = () => {
        reject('Failed to load image');
      };

      image.src = base64Image;
    });
  }
}
